import type { AppConfig } from './types';
import { ALL_REGISTRABLE_COUNTRIES } from '../../helpers/appCountries';

// Brand tokens aligned with the ledger17 homepage (ledger17-homepage:
// static/css/custom.css and the amber ramp in assets/docs/scss/_variables.scss),
// the canonical statement of the brand. Dark-first: amber-gold accent
// (#D4A843) on near-black surfaces, monospace display type.
const IBM_PLEX_MONO = '"IBM Plex Mono", "Fira Code", ui-monospace, SFMono-Regular, Menlo, monospace';
const IBM_PLEX_SANS = '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export const ledger17App: AppConfig = {
    id: 'ledger17',
    productName: 'ledger17',
    htmlTitle: 'Admin - ledger17',
    homepageUrl: 'https://www.ledger17.com',
    // API-only today: ledger17 ships as a headless API and this orgadmin instance is
    // its only GUI, so there is nowhere to hand the user back to. Declared rather than
    // left to the absence of appLinkUrl, which VITE_DEFAULT_APP_URL or a `?returnUrl=`
    // would otherwise override. Flip to true (and add appLinkUrl) if a ledger17 app ships.
    showBackToApp: false,
    logo: { src: '/apps/ledger17/favicon.svg', alt: 'ledger17' },
    favicon: '/apps/ledger17/favicon.svg',
    brand: {
        light: {
            // Light mode mirrors the homepage docs theme: neutral white/gray
            // surfaces with the brand amber darkened along the homepage's own
            // ramp — amber-800/500/900 — for WCAG AA contrast on white.
            primary: { main: '#795e20', light: '#d4a843', dark: '#574419', contrastText: '#ffffff' },
            // Homepage light secondary is neutral gray (Bootstrap gray-600/500/700).
            secondary: { main: '#6c757d', light: '#adb5bd', dark: '#495057', contrastText: '#ffffff' },
            background: { default: '#f8f9fa', paper: '#ffffff' },
        },
        dark: {
            // --l17-accent on --l17-bg/--l17-surface; light = amber-400 from the
            // homepage ramp, dark = the homepage's primary-button hover (#c19733).
            primary: { main: '#d4a843', light: '#e2be69', dark: '#c19733', contrastText: '#0a0806' },
            // Neutral surface tones (--l17-border-strong / dim / border): the
            // homepage has no second accent — outline buttons are hairline gray.
            secondary: { main: '#2a2a2a', light: '#787878', dark: '#1e1e1e', contrastText: '#f0f0f0' },
            background: { default: '#090909', paper: '#111111' },
        },
        // Headings/display in mono (--l17-mono), long-form body in sans — same
        // split the homepage makes between brand chrome and docs body text.
        fonts: { heading: IBM_PLEX_MONO, body: IBM_PLEX_SANS },
        // The homepage hero: near-black with a faint amber wash fading into the
        // page background.
        heroGradient:
            'radial-gradient(1100px 520px at 85% -10%, rgba(212, 168, 67, 0.16), transparent 60%), linear-gradient(180deg, #181818 0%, #090909 55%, #090909 100%)',
    },
    defaultThemeMode: 'dark',
    // Organisation self-management only: no taxonomies, no processes, and no
    // access grants (decided in design.md's resolved open questions).
    enabledResources: new Set([
        'settings',
        'billing',
        'user',
        'api-key',
        'category/location',
        'category/department',
    ]),
    signup: {
        tiers: [
            {
                // Must match the backend's SubscriptionLevel constant exactly: this string
                // is posted as `subscriptionLevel` at signup, and a value no App claims
                // creates no entitlement at all — so the customer pays, sees the plan in
                // the billing form, and holds none of the permissions it gates. The
                // earlier `LEDGER_STANDARD` spelling did precisely that.
                id: 'LEDGER_API',
                i18nKey: 'ledgerApi',
                // Two independent price points, not a conversion: NOK 99 is roughly
                // NOK 15 short of EUR 9.90 at any real rate. Norway is billed in
                // kroner, everywhere else in euro — see `currencyForCountry`.
                prices: { NOK: 99, EUR: 9.9 },
                featureCount: 4,
                highlighted: true,
                // Billed per step: each 100 new / 6 000 stored transactions adds
                // another step at the tier price. Display only — the backend does
                // not meter steps yet.
                metered: { newTransactions: 100, storedTransactions: 6000 },
            },
            {
                // Attaching vouchers (Norwegian "bilag") to transactions. Sold on the
                // backend's WRITE_VOUCHERS permission at LEDGER17 rank 2 — this id
                // must match SubscriptionLevel.LEDGER_API_DOCUMENTS exactly, or the
                // customer pays and receives no entitlement at all.
                id: 'LEDGER_API_DOCUMENTS',
                i18nKey: 'ledgerApiDocuments',
                prices: { NOK: 149, EUR: 14.9 },
                featureCount: 5,
                // The base plan keeps the badge and the default selection: this is an
                // opt-up, not the tier a new customer should land on.
                highlighted: false,
                // Same volume as the base plan — the price buys the capability, not
                // more transactions. Keep the two steps identical.
                metered: { newTransactions: 100, storedTransactions: 6000 },
            },
        ],
        // Sold anywhere. A pure API ledger has no Norwegian dependency — no company
        // register to consult, no national reporting to file — so restricting it to
        // a country list would be an invented constraint. Matches the *absence* of a
        // `countries.app.ledger17` key in sirk-orgadmin-backend, which means the same
        // thing there.
        countries: ALL_REGISTRABLE_COUNTRIES,
    },
    defaultTaxonomies: [],
};
